import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  CssBaseline,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Button,
  Avatar,
  Menu,
  MenuItem
} from '@mui/material';
import {
  Menu as MenuIcon,
  ChevronLeft as ChevronLeftIcon,
  Person as PersonIcon,
  Collections as CollectionsIcon,
  Settings as SettingsIcon,
  Mail as MailIcon,
  Logout as LogoutIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useAuth } from '../../context/auth/CognitoAuthContext';
import { useImages } from '../../context/images/ImagesContext';
import InfluencerSelector from '../common/InfluencerSelector';

const drawerWidth = 240;

const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    flexGrow: 1,
    padding: theme.spacing(3),
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    marginLeft: `-${drawerWidth}px`,
    ...(open && {
      transition: theme.transitions.create('margin', {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen,
      }),
      marginLeft: 0,
    }),
  }),
);

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
  justifyContent: 'flex-end',
}));

const Layout = () => {
  const { currentUser, logout } = useAuth();
  const { selectedInfluencer } = useImages();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleProfileMenuClose();
    logout();
    navigate('/login');
  };


  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerOpen}
            edge="start"
            sx={{ mr: 2, ...(open && { display: 'none' }) }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            OhlyFans Image Manager
          </Typography>
          {selectedInfluencer && (
            <Typography variant="subtitle1" sx={{ mr: 2 }}>
              {selectedInfluencer.name}
            </Typography>
          )}
          <IconButton
            size="large"
            edge="end"
            aria-label="account of current user"
            aria-controls="menu-appbar"
            aria-haspopup="true"
            onClick={handleProfileMenuOpen}
            color="inherit"
          >
            <Avatar sx={{ bgcolor: 'secondary.main' }}>
              {currentUser?.name?.charAt(0) || 'U'}
            </Avatar>
          </IconButton>
          <Menu
            id="menu-appbar"
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={Boolean(anchorEl)}
            onClose={handleProfileMenuClose}
          >
            <MenuItem onClick={handleProfileMenuClose}>
              <ListItemIcon>
                <PersonIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Profile</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Logout</ListItemText>
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      <Drawer
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
          },
        }}
        variant="persistent"
        anchor="left"
        open={open}
      >
        <DrawerHeader>
          <IconButton onClick={handleDrawerClose}>
            <ChevronLeftIcon />
          </IconButton>
        </DrawerHeader>
        <Divider />
        <Box sx={{ p: 2 }}>
          <InfluencerSelector />
        </Box>
        <Divider />
        <List>
          {/* Always show Influencer Settings */}
          <ListItem disablePadding>
            <ListItemButton
              selected={location.pathname === '/'}
              onClick={() => navigate('/')}
            >
              <ListItemIcon><PersonIcon /></ListItemIcon>
              <ListItemText primary="Influencer List" />
            </ListItemButton>
          </ListItem>
          
          {/* Show tabs only when an influencer is selected */}
          {selectedInfluencer && (
            <>
              <Divider />
              <ListItem>
                <Typography variant="subtitle2" color="text.secondary">
                  {selectedInfluencer.name} Tabs
                </Typography>
              </ListItem>
              
              <ListItem disablePadding>
                <ListItemButton
                  selected={location.pathname.includes('/about')}
                  onClick={() => navigate(`/influencer/${selectedInfluencer.id}/about`)}
                >
                  <ListItemIcon><PersonIcon /></ListItemIcon>
                  <ListItemText primary="About" />
                </ListItemButton>
              </ListItem>
              
              <ListItem disablePadding>
                <ListItemButton
                  selected={location.pathname.includes('/raw-dataset')}
                  onClick={() => navigate(`/influencer/${selectedInfluencer.id}/raw-dataset`)}
                >
                  <ListItemIcon><CollectionsIcon /></ListItemIcon>
                  <ListItemText primary="Raw Dataset" />
                </ListItemButton>
              </ListItem>
              
              
              <ListItem disablePadding>
                <ListItemButton
                  selected={location.pathname.includes('/training-settings')}
                  onClick={() => navigate(`/influencer/${selectedInfluencer.id}/training-settings`)}
                >
                  <ListItemIcon><SettingsIcon /></ListItemIcon>
                  <ListItemText primary="Training Settings" />
                </ListItemButton>
              </ListItem>
            </>
          )}
          
          {/* Always show Generate Invitation */}
          <Divider />
          <ListItem disablePadding>
            <ListItemButton
              selected={location.pathname === '/invitation'}
              onClick={() => navigate('/invitation')}
            >
              <ListItemIcon><MailIcon /></ListItemIcon>
              <ListItemText primary="Generate Invitation" />
            </ListItemButton>
          </ListItem>
        </List>
      </Drawer>
      <Main open={open}>
        <DrawerHeader />
        <Outlet />
      </Main>
    </Box>
  );
};

export default Layout;
